/* @flow */

import type { Context } from './Common';
import { GridWidth, GridHeight } from './Common';

export default class ZoomContext implements Context {
  canvasElm: HTMLCanvasElement;
  context: CanvasRenderingContext2D;

  xRatio: number;
  yRatio: number;
  canvasW: number;
  canvasH: number;
  offsetX: number;
  offsetY: number;

  constructor(canvasElm: HTMLCanvasElement) {
    this.canvasElm = canvasElm;
    this.ensureCanvasDimensions();
    this.context = this.canvasElm.getContext('2d');
    window.addEventListener('resize', () => {
      this.ensureCanvasDimensions();
    });
  }
  ensureCanvasDimensions() {
    const { canvasElm } = this;
    const body = document.body || {};

    const canvasW = body.clientWidth;
    const canvasH = body.clientHeight;

    // only set on change, setting clears the canvas and introduces jaggies
    if (canvasW !== canvasElm.width) canvasElm.width = canvasW;
    if (canvasH !== canvasElm.height) canvasElm.height = canvasH;

    // enforce ratio by shrinking the larger side
    let displayW = canvasW;
    let displayH = canvasH;
    const targetRatio = GridWidth / GridHeight;
    const currentRatio = displayW / displayH;
    if (currentRatio > targetRatio) {
      // screen too wide, need to shrink width to match width
      displayW = targetRatio * displayH;
    } else {
      // screen too skinny, need to shrink height to match width
      displayH = displayW / targetRatio;
    }

    this.canvasW = canvasW;
    this.canvasH = canvasH;
    this.offsetX = (canvasW - displayW) / 2;
    this.offsetY = (canvasH - displayH) / 2;
    this.xRatio = displayW / GridWidth;
    this.yRatio = displayH / GridHeight;
  }
  goFullScreen() {
    const { canvasElm } = this;
    // https://stackoverflow.com/a/16124664/6461842
    // $FlowFixMe
    if (canvasElm.requestFullScreen) canvasElm.requestFullScreen();
    // $FlowFixMe
    else if (canvasElm.webkitRequestFullScreen) canvasElm.webkitRequestFullScreen();
    // $FlowFixMe
    else if (canvasElm.mozRequestFullScreen) canvasElm.mozRequestFullScreen();
  }

  drawFrame() {
    this.fillStyle = '#222222';
    this.context.fillRect(0, 0, this.canvasW, this.offsetY);
    this.context.fillRect(0, this.canvasH - this.offsetY, this.canvasW, this.offsetY);
    this.context.fillRect(0, 0, this.offsetX, this.canvasH);
    this.context.fillRect(this.canvasW - this.offsetX, 0, this.offsetX, this.canvasH);
  }

  // wrapping canvas funcs

  set fillStyle(value: string) {
    this.context.fillStyle = value;
  }
  set strokeStyle(value: string) {
    this.context.strokeStyle = value;
  }
  set lineWidth(value: number) {
    this.context.lineWidth = value * this.xRatio;
  }
  set textAlign(value: string) {
    this.context.textAlign = value;
  }
  set fontSize(value: number) {
    this.context.font = `${value * this.yRatio}px Arial`;
  }
  beginPath() {
    this.context.beginPath();
  }
  moveTo(x: number, y: number) {
    this.context.moveTo(this.offsetX + x * this.xRatio, this.offsetY + y * this.yRatio);
  }
  lineTo(x: number, y: number) {
    this.context.lineTo(this.offsetX + x * this.xRatio, this.offsetY + y * this.yRatio);
  }
  stroke() {
    this.context.stroke();
  }
  fill() {
    this.context.fill();
  }
  fillText(value: string, x: number, y: number) {
    this.context.fillText(value, this.offsetX + x * this.xRatio, this.offsetY + y * this.yRatio);
  }
  strokeRect(x: number, y: number, width: number, height: number) {
    this.context.strokeRect(
      this.offsetX + x * this.xRatio,
      this.offsetY + y * this.yRatio,
      width * this.xRatio,
      height * this.yRatio
    );
  }
  fillRect(x: number, y: number, width: number, height: number) {
    this.context.fillRect(
      this.offsetX + x * this.xRatio,
      this.offsetY + y * this.yRatio,
      width * this.xRatio,
      height * this.yRatio
    );
  }
  drawImage(
    image: Image,
    x1: number,
    y1: number,
    w1: number,
    h1: number,
    x2: number,
    y2: number,
    w2: number,
    h2: number
  ) {
    this.context.drawImage(
      image,
      x1,
      y1,
      w1,
      h1,
      this.offsetX + x2 * this.xRatio,
      this.offsetY + y2 * this.yRatio,
      w2 * this.xRatio,
      h2 * this.xRatio
    );
  }
}



// WEBPACK FOOTER //
// ./src/ZoomContext.js